import cx from 'classnames'
import { motion } from 'motion/react'
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { accordionContentAnimation } from '@lib/animate'

import Icon from '@components/icon'

interface AccordionProps {
  id: string
  title?: ReactNode
  open?: boolean
  info?: string
  isOpenByDefault?: boolean
  bordered?: boolean
  borderBottom?: boolean
  hideOverflow?: boolean
  className?: string
  innerClassName?: string
  children?: ReactNode
  onToggle?: (isOpen: boolean) => void
}

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      id,
      title,
      open,
      info,
      isOpenByDefault = false,
      bordered,
      borderBottom,
      hideOverflow,
      className,
      innerClassName,
      children,
      onToggle,
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(isOpenByDefault)

    const isControlled = useMemo(() => typeof open === 'boolean', [open])
    const isOpen = useMemo(
      () => (isControlled ? open : internalOpen),
      [internalOpen, isControlled, open],
    )

    const handleToggle = useCallback(() => {
      if (onToggle) {
        onToggle(!isOpen)
      }

      if (!isControlled) {
        setInternalOpen((currentInternalOpen) => !currentInternalOpen)
      }
    }, [isControlled, isOpen, onToggle])

    return (
      <div
        ref={ref}
        className={cx(
          'border-divider',
          {
            'border-b': borderBottom,
            'border-t': !borderBottom,
            'border rounded': bordered,
          },
          className,
        )}
      >
        <button
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls={`accordion-${id}`}
          className={cx(
            'text-left flex justify-between items-center py-5 w-full font-medium',
            {
              'px-4': bordered,
            },
          )}
        >
          <h4
            className={cx('text-base', {
              'inline-flex items-center gap-1.5': !!info,
            })}
          >
            {!!info && (
              <Icon
                name="Info"
                id="frame-color-icon"
                className="text-gray-500 text-lg shrink-0"
              />
            )}
            {title}
          </h4>

          <div className="relative w-3 h-3 transition-transform duration-300 ease-in-out">
            <span className="absolute top-1/2 left-0 w-full h-[1.5px] bg-current transform -translate-y-1/2" />
            <span
              className={cx(
                'absolute left-1/2 top-0 h-full w-[1.5px] bg-current transform -translate-x-1/2 transition-all duration-300',
                {
                  'rotate-90': isOpen,
                },
              )}
            />
          </div>
        </button>

        <motion.div
          id={`accordion-${id}`}
          initial={isOpenByDefault ? 'open' : 'closed'}
          animate={isOpen ? 'open' : 'closed'}
          variants={accordionContentAnimation}
          transition={{
            duration: 0.5,
            ease: [0.19, 1.0, 0.22, 1.0],
          }}
          className={cx({
            'overflow-hidden': !isOpen || hideOverflow,
          })}
        >
          <div
            className={cx(
              'pb-6',
              {
                'px-4': bordered,
              },
              innerClassName,
            )}
          >
            {children}
          </div>
        </motion.div>
      </div>
    )
  },
)

Accordion.displayName = 'Accordion'

export default Accordion
